/** Si el build no inyecta REACT_APP_PORTAL_API_URL, sin fallback las peticiones van al mismo origen y el login falla. */
const DEFAULT_PORTAL_API_BASE =
  "https://n4qh9nwqv5.execute-api.us-east-1.amazonaws.com/prod";

function getApiBase() {
  const fromEnv = process.env.REACT_APP_PORTAL_API_URL;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim().replace(/\/$/, "");
  }
  if (typeof window !== "undefined" && window.__PORTAL_API_URL__) {
    return String(window.__PORTAL_API_URL__).trim().replace(/\/$/, "");
  }
  return DEFAULT_PORTAL_API_BASE.replace(/\/$/, "");
}

/** Rutas donde no debe enviarse JWT (evita tokens caducados y conflictos con proxies). */
function isPublicAuthPath(path) {
  const p = path.split("?")[0];
  return (
    p === "/auth/login" ||
    p === "/auth/register" ||
    p === "/auth/forgot-password" ||
    p === "/auth/reset-password" ||
    p === "/revista/subscribe" ||
    p === "/revista/current" ||
    p === "/revista/issues" ||
    p === "/revista/unsubscribe"
  );
}

async function request(path, options = {}) {
  const url = `${getApiBase()}${path.startsWith("/") ? path : `/${path}`}`;
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };
  const token = localStorage.getItem("portal_token");
  if (token && !isPublicAuthPath(path)) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(url, { ...options, headers });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { error: text };
  }
  if (!res.ok) {
    const err = new Error(data?.error || data?.detail || res.statusText);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function buildQuery(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export const portalApi = {
  register: (body) =>
    request("/auth/register", { method: "POST", body: JSON.stringify(body) }),
  forgotPassword: (body) =>
    request("/auth/forgot-password", { method: "POST", body: JSON.stringify(body) }),
  resetPassword: (body) =>
    request("/auth/reset-password", { method: "POST", body: JSON.stringify(body) }),
  login: (body) =>
    request("/auth/login", { method: "POST", body: JSON.stringify(body) }),
  me: () => request("/auth/me"),
  logout: () => request("/auth/logout", { method: "POST" }),
  listPsychologists: () => request("/psychologists"),
  availability: (psychologistId, date) =>
    request(
      `/appointments/availability?psychologistId=${encodeURIComponent(
        psychologistId
      )}&date=${encodeURIComponent(date)}`
    ),
  createAppointment: (body) =>
    request("/appointments", { method: "POST", body: JSON.stringify(body) }),
  myAppointments: () => request("/appointments/mine"),
  cancelAppointment: (appointmentId) =>
    request(`/appointments/${appointmentId}`, { method: "DELETE" }),
  adminClients: (params = {}) => request(`/admin/clients${buildQuery(params)}`),
  adminClient: (userId) =>
    request(`/admin/clients/${encodeURIComponent(userId)}`),
  adminClientAppointments: (userId) =>
    request(`/admin/clients/${encodeURIComponent(userId)}/appointments`),
  adminCreateClient: (body) =>
    request("/admin/clients", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  adminPsychologists: (params = {}) =>
    request(`/admin/psychologists${buildQuery(params)}`),
  adminPsychologist: (psychologistId) =>
    request(`/admin/psychologists/${encodeURIComponent(psychologistId)}`),
  adminCreatePsychologist: (body) =>
    request("/admin/psychologists", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  adminUpdatePsychologist: (body) =>
    request("/admin/psychologists", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  adminDeletePsychologist: (psychologistId) =>
    request(`/admin/psychologists/${encodeURIComponent(psychologistId)}`, {
      method: "DELETE",
    }),
  adminPsychologistPhotoUploadUrl: (body) =>
    request("/admin/psychologists/photo-upload-url", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  adminAppointments: (params = {}) =>
    request(`/admin/appointments${buildQuery(params)}`),
  psychologistProfile: () => request("/psychologist/profile"),
  psychologistProfilePut: (body) =>
    request("/psychologist/profile", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  psychologistPhotoUploadUrl: (body) =>
    request("/psychologist/profile/photo-upload-url", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  clientPhotoUploadUrl: (body) =>
    request("/client/profile/photo-upload-url", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  clientProfilePut: (body) =>
    request("/client/profile", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  psychologistAppointments: () => request("/psychologist/appointments"),
  revistaSubscribe: (body) =>
    request("/revista/subscribe", { method: "POST", body: JSON.stringify(body) }),
  revistaCurrent: () => request("/revista/current"),
  revistaIssues: () => request("/revista/issues"),
  revistaUnsubscribe: (body) =>
    request("/revista/unsubscribe", { method: "POST", body: JSON.stringify(body) }),
  adminRevistaIssues: () => request("/admin/revista/issues"),
  adminRevistaIssue: (issueId) =>
    request(`/admin/revista/issues/${encodeURIComponent(issueId)}`),
  adminRevistaCreateIssue: (body) =>
    request("/admin/revista/issues", { method: "POST", body: JSON.stringify(body) }),
  adminRevistaUploadUrl: (issueId, body) =>
    request(`/admin/revista/issues/${encodeURIComponent(issueId)}/upload-url`, {
      method: "POST",
      body: JSON.stringify(body || {}),
    }),
  adminRevistaPublish: (issueId, body = {}) =>
    request(`/admin/revista/issues/${encodeURIComponent(issueId)}/publish`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  adminRevistaDeleteIssue: (issueId) =>
    request(`/admin/revista/issues/${encodeURIComponent(issueId)}`, {
      method: "DELETE",
    }),
  adminRevistaSubscribers: (params = {}) =>
    request(`/admin/revista/subscribers${buildQuery(params)}`),
};
